import { useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import BlingMirrorQueuePanel from '../../components/warehouse/shipping/BlingMirrorQueuePanel';
import ShippingWorkspace from '../../components/warehouse/shipping/ShippingWorkspace';
import { useShippingLabels } from '../../components/warehouse/shipping/shippingLabels';
import { useAuth } from '../../hooks/useAuth';

const TAB_SHIPMENTS = 'shipments';
const TAB_BLING_MIRROR = 'bling_mirror';
const TAB_IDS = [TAB_SHIPMENTS, TAB_BLING_MIRROR];

/**
 * /warehouse/shipping — CONSOLE DE EXPEDIÇÃO (Supervisor, desktop).
 *
 * Duas abas, na ordem do trabalho:
 *   1. **Embarques** — abrir o carregamento de um romaneio que já existe, ver o
 *      checklist (o que deve embarcar × o que já foi bipado) e fechar. O
 *      fechamento é o ato que baixa estoque, fecha os pallets e dispara o
 *      espelho do Bling — e ele só habilita com 100% bipado.
 *   2. **Espelho Bling** — o que o fechamento enfileirou e o que aconteceu com
 *      cada linha. É a tela que responde "por que o Bling não atualizou?".
 *
 * ── Permissão ───────────────────────────────────────────────────────────────
 * `wms:manage:shipping` (expandida por `wms:manage`), como as outras páginas do
 * módulo. O BIPE do carregamento é do chão (`wms:operate`, no coletor): quem
 * carrega não dá baixa.
 *
 * ── O que esta página NÃO é ─────────────────────────────────────────────────
 * Não é um segundo módulo de romaneio (§7). Transportadora, motorista, placa,
 * PDF, itens por SKU e a galeria de mídia continuam na tela de romaneio
 * (`/wms/outbound/:id`), que é dona deles. Aqui vive o que o romaneio nunca
 * soube: o que entrou fisicamente no veículo.
 */
const WarehouseShippingPage = () => {
    const { label } = useShippingLabels();
    const { checkPermission } = useAuth();
    const [activeTab, setActiveTab] = useState(TAB_SHIPMENTS);

    // A MESMA permissão que o backend exige para abrir, acrescentar item,
    // fechar e cancelar. Lida aqui (e não presumida pela rota) porque a rota
    // também aceita quem só tem `wms:operate` para LER — e essa pessoa não
    // pode ver botões que o servidor vai recusar.
    const canManage = checkPermission('wms:manage:shipping');

    return (
        <div className="space-y-6">
            <PageHeader title={label('wms.shipping.title')} subtitle={label('wms.shipping.description')} />

            <div className="border-b border-mono-200 dark:border-mono-800">
                <nav className="flex space-x-8 overflow-x-auto">
                    {TAB_IDS.map((tabId) => {
                        const isActive = tabId === activeTab;
                        return (
                            <button
                                type="button"
                                key={tabId}
                                onClick={() => setActiveTab(tabId)}
                                aria-current={isActive ? 'true' : undefined}
                                className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                                    isActive
                                        ? 'border-black text-black dark:border-white dark:text-white'
                                        : 'border-transparent text-mono-500 hover:border-mono-300 hover:text-mono-700 dark:hover:text-mono-300'
                                }`}
                            >
                                {label(`wms.tab.${tabId}`)}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {activeTab === TAB_SHIPMENTS ? (
                <ShippingWorkspace label={label} canManage={canManage} />
            ) : (
                <BlingMirrorQueuePanel label={label} />
            )}
        </div>
    );
};

export default WarehouseShippingPage;
